import { axiosPrivate } from '@/shared/api/axios'
import { API } from '@/shared/constants/api'

import type {
  IAvailabilityMetrics,
  IInventoryComment,
  IInventoryDetail,
  IInventoryItem,
  IListReservationHistory,
  IMonthlyActivity,
  IReservation
} from '../models/inventory'

export const getAvailability = async (): Promise<IInventoryItem[]> => {
  const response = await axiosPrivate.get<IInventoryItem[]>(API.availability.root)
  return response.data
}

export const getPropertyDetail = async (propertyId: string): Promise<IInventoryDetail> => {
  const response = await axiosPrivate.get<IInventoryDetail>(API.availability.byId(propertyId))
  return response.data
}

export const getAvailabilityMetrics = async (): Promise<IAvailabilityMetrics> => {
  const response = await axiosPrivate.get<IAvailabilityMetrics>(API.availability.metrics)
  return response.data
}

export const getMonthlyActivity = async (): Promise<IMonthlyActivity> => {
  const response = await axiosPrivate.get<IMonthlyActivity>(API.availability.monthlyActivity)
  return response.data
}

export const createReservation = async (data: {
  property: string
  reservedDates: string[]
  notes?: string
}): Promise<{ id: string }> => {
  const response = await axiosPrivate.post(API.reservations.root, data)
  return response.data
}

export const releaseReservation = async (
  reservationId: string,
  notes: string
): Promise<{ success: boolean }> => {
  const response = await axiosPrivate.patch(API.reservations.release(reservationId), { notes })
  return response.data
}

export const resendAuthorizationEmails = async (
  reservationId: string
): Promise<{ success: boolean }> => {
  const response = await axiosPrivate.post(API.reservations.resendAuthorization(reservationId))
  return response.data
}

export const authorizeReservation = async (
  reservationId: string,
  notes?: string
): Promise<{ success: boolean }> => {
  const response = await axiosPrivate.patch(API.reservations.authorizeById(reservationId), {
    notes
  })
  return response.data
}

export const declineReservation = async (
  reservationId: string,
  notes: string
): Promise<{ success: boolean }> => {
  const response = await axiosPrivate.patch(API.reservations.declineById(reservationId), { notes })
  return response.data
}

export const getReservationByProperty = async (
  propertyId: string
): Promise<IReservation | null> => {
  try {
    const response = await axiosPrivate.get<IReservation>(API.reservations.byProperty(propertyId))
    return response.data
  } catch {
    return null
  }
}

export const getReservationHistory = async (
  propertyId: string
): Promise<IListReservationHistory[]> => {
  const response = await axiosPrivate.get<IListReservationHistory[]>(
    API.reservations.history(propertyId)
  )
  return response.data
}

export const getInventoryComments = async (propertyId: string): Promise<IInventoryComment[]> => {
  const response = await axiosPrivate.get<IInventoryComment[]>(
    API.inventoryComments.byProperty(propertyId)
  )
  return response.data
}

export const createInventoryComment = async (
  propertyId: string,
  data: { body: string; parent?: string }
): Promise<IInventoryComment> => {
  const response = await axiosPrivate.post<IInventoryComment>(
    API.inventoryComments.byProperty(propertyId),
    data
  )
  return response.data
}

export const deleteInventoryComment = async (commentId: string): Promise<{ success: boolean }> => {
  const response = await axiosPrivate.delete<{ success: boolean }>(
    API.inventoryComments.byId(commentId)
  )
  return response.data
}
